<template>
  <v-col class="my-0 py-0">
    <v-row>
    <div v-if="loadingAltiris">
      <v-progress-circular
        indeterminate
        color="primary"
        size="20"
        class="mb-2"
      >
      </v-progress-circular>
       {{$t('components.altiris.load')}}
    </div>
    <div v-else>
      <span
        v-if="others.length > 0"
      >
        {{$t('components.altiris.result')}}
      </span>
      <span
        v-else
      >
        {{$t('components.altiris.notFound')}}
      </span>
      <v-col>
        <a
          v-for="(item, i) in others"
          :key="i"
          @click="setValue(item)"
        >
          <v-icon>
            mdi-laptop
          </v-icon>
          {{$t('components.altiris.show',[item.name, item.fechUltLogin])}}
          <br>
          <span v-if="item.edificio">
            {{$t('components.altiris.edificio')}} {{ item.edificio }}
          </span>
          <br>
          <span v-if="item.floor">
            {{$t('components.altiris.floor')}} {{ item.floor }}
          </span>
          <br>
          <span v-if="item.room">
            {{$t('components.altiris.room')}} {{ item.room }}
          </span>
          <v-spacer></v-spacer>
        </a>
      </v-col>
    </div>
    </v-row>
    <v-row>
      <v-autocomplete
        :outlined="!disabled"
        :filled="disabled"
        ref="autocomplete"
        v-model="value"
        :search-input.sync="query"
        :hide-details="'auto'"
        :label="label"
        :persistent-hint="hint !== ''"
        :hint="hint"
        :disabled="disabled"
        :loading="loading"
        :items="items"
        :placeholder="params.placeholder || label"
        :item-text="itemText"
        :item-value="params.value || 'id'"
        :multiple="isMultiple"
        :no-filter="true"
        :clearable="!required"
        :no-data-text="messageEmpty"
        :rules="rules"
        :hide-no-data="!isEmpty"
        @click="isEmpty = false"
        @keydown="handleSearch($event)"
        @change="emitInputData"
      >
        <template v-if="$slots.required" v-slot:prepend-inner>
          <slot name="required"></slot>
        </template>
        <template v-slot:no-data v-if="params.nodatamessage">
          <v-list-item>
            <v-list-item-title>
              {{params.nodatamessage}}
              <strong v-if="params.extraFilter">
                {{$t('components.search.noDataMessage')}}
                <v-icon
                  size="20"
                >
                  mdi-filter-plus-outline
                </v-icon>
                {{$t('components.search.noDataMessageContinue')}}
            </strong>
            </v-list-item-title>
          </v-list-item>
        </template>
        <template v-slot:append >
          <v-icon v-if="!loading">mdi-magnify</v-icon>
          <v-progress-circular
            v-else
            color="primary"
            indeterminate
            size="25"
          >
          </v-progress-circular>
        </template>
        <template v-slot:append-outer>
          <v-btn
            icon
            color="primary"
            @click="barCodeScannerClick()"
            v-if="isMobile"
          >
            <v-icon
              size="30"
              class="mb-4"
            >
              mdi-camera
            </v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </v-row>
    <v-row>
      <span v-if='barCodeNotFound'>
      {{ $t('components.barCodeScanner.barCodeNotFound', [barCode]) }}
      </span>
    </v-row>
  </v-col>
</template>
<script>
import Vue from 'vue';
import Search from '../field-types/Search.vue';

export default {
  name: 'BarCodeScanner',
  extends: Search,
  data() {
    return {
      barCode: null,
      barCodeNotFound: false,
    };
  },
  computed: {
    isMobile() {
      const getter = this.$store.getters['globalState/get'];
      return getter('deviceOS') != null;
    },
  },
  methods: {
    barCodeScannerClick() {
      Vue.prototype.$flutterPort.postMessage('flutterOpenBarCodeScanner');
    },

    async searchAltirisBarCode(barCode) {
      this.barCode = barCode;
      try {
        this.barCodeNotFound = false;
        const filters = {
          inactive: 0,
          resource_class: {
            $in: [1000091, 1000597, 1000046],
          },
          status: 2606,
          $or: [
            { name: { $regex: `%${barCode}%` } },
            { zcod_sas: { $regex: `%${barCode}%` } },
            { serial_number: { $regex: `%${barCode}%` } }],
        };
        const result = await this.search({ filters });
        this.barCodeNotFound = result.length === 0;
        const valueItem = result.unique();
        this.value = valueItem;
        this.defaultItem = valueItem;

        this.items = result.list;
        this.lastSearch = this.itemText(valueItem);

        // force update to emit item and value event to update context
        this.$emit('update:inputData', {
          value: this.valueAttributeItem(this.value),
          item: this.value,
        });
      } catch (e) {
        this.barCodeNotFound = true;
      }
    },
  },

  async created() {
    this.loadingAltiris = true;
    this.client = this.$api.getClient('search');
    const { initialsearch } = this.params;
    let result = null;

    // if initial value provided we will make a search to select the default row
    if (this.initialValue) {
      const resultValue = /^\+?(0|[1-9]\d*)$/.test(this.initialValue) ? parseInt(this.initialValue, 10) : `${this.initialValue}`;
      const {
        value, filters, apicges,
      } = this.params;

      /// we need to save a copy of evalued request params to detect
      /// when the component needs to be auto-updated
      let evaluedApicges;
      if (apicges) {
        evaluedApicges = this.evalParam(apicges);
        this.searchArguments.apicges = evaluedApicges;
      }
      const initialValueProperty = this.params.initialvalueproperty || value || 'id';
      let evaluedFilters = {};
      if (initialValueProperty !== 'id') {
        evaluedFilters = this.evalParam(filters);
        this.searchArguments.filters = JSON.parse(JSON.stringify(evaluedFilters));
      }

      evaluedFilters[initialValueProperty] = resultValue;
      /// end

      // initial value search
      try {
        result = await this.search({ apicges: evaluedApicges, filters: evaluedFilters });
      } catch (ex) {
        console.error('error retrieving initial value search', ex);
      }

    // component could contain an initial search to suggest some options without input
    } else if (initialsearch != null) {
      const { entity, params } = this.evalParam(initialsearch);
      // 1000118
      if (entity == null || params == null) {
        console.error('invalid initial search configuration: entity and params fields are mandatory');
        return;
      }

      try {
        result = await this.client.search({ target: 'namedquery', entity, filters: params });
        console.log('initial search result', result);
      } catch (ex) {
        console.error('error in initialsearch', ex);
      }
    }

    if (result != null && result.total > 0) {
      const valueItem = result.unique();
      this.value = valueItem;
      this.defaultItem = valueItem;

      this.items = result.list;
      this.lastSearch = this.itemText(valueItem);

      // force update to emit item and value event to update context
      this.$emit('update:inputData', {
        value: this.valueAttributeItem(this.value),
        item: this.value,
      });
    }
  },
};
</script>
